import React, { useState, useEffect, useContext } from 'react';
import moment from 'moment';
import { styled, Box, Typography } from '@mui/material';
import LocalizationContext from '../../../../localization/LocalizationContext';
import getHistory from '../../../../repository/getHistory';
import { capitalize } from '../../../../../utils';

const HistoryList = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 12,
    marginTop: 12,
    backgroundColor: '#FAFAFA',
    borderRadius: 5,
    maxHeight: 300,
    overflow: 'scroll',
}));

const HistoryItem = styled(Box)(() => ({
    backgroundColor: '#FFFFFF',
    borderRadius: 5,
    padding: 12,
    border: '1px solid rgba(0, 0, 0, 0.12)',
}));

const renderValue = (value) => {
    if (Array.isArray(value)) {
        return (
            <Box component="span" sx={{ ml: 1 }}>
                [
                {value.map((v, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <span key={`${i}-${typeof v}`}>
                        {typeof v === 'object' && v !== null ? renderValue(v) : String(v)}
                        {i < value.length - 1 ? ', ' : ''}
                    </span>
                ))}
                ]
            </Box>
        );
    }
    if (typeof value === 'object' && value !== null) {
        return (
            <Box component="span" sx={{ ml: 1 }}>
                {'{ '}
                {Object.entries(value).map(([k, v], idx, arr) => (
                    <span key={k}>
                        <strong>{k}</strong>: {renderValue(v)}
                        {idx < arr.length - 1 ? ', ' : ''}
                    </span>
                ))}
                {' }'}
            </Box>
        );
    }
    return value ?? '—';
};

const formatFieldName = (field, localization) => {
    const overrides = {
        po_number: localization?.general?.customer_po,
        hauling_id: localization?.general?.hauling_singular,
        processor_id: localization?.general?.processor_singular,
    };

    if (overrides[field]) {
        return overrides[field];
    }

    return field
        .split('_')
        .filter((part) => part.toLowerCase() !== 'id')
        .map((part) => {
            if (part.length <= 2) {
                return part.toUpperCase();
            }
            return capitalize(part);
        })
        .join(' ');
};

/**
 * @param {Object} props
 * @param {boolean} isEdit - Whether the item is being edited (vs created)
 * @param {Object} item - The item being edited
 * @param {Object} props.props - Additional config
 * @param {string} [props.props.renderField] - If present, only render if this field exists on the item
 * @param {string} props.props.type - The type of document history (e.g. 'schedule-supply-item')
 * @param {string[]} [props.props.omitHistoryKeys] - If keys are present in history delta, the history will be omitted
 * @param {string[]} [props.props.omitFields] - If field is present in history delta, the field will be omitted
 * @param {string} [props.props.label] - Section label to display above the history list
 */
const ItemEditHistory = ({ isEdit, item, props }) => {
    const { renderField, type, omitHistoryKeys = [], omitFields = [], label } = props;
    const localization = useContext(LocalizationContext);

    if (!isEdit || (renderField && !item[renderField])) {
        return <></>;
    }

    const [itemHistory, setItemHistory] = useState();
    const itemId = item._id || item.id;

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                let histories = await getHistory(itemId, type);

                // filter out histories that contain omitHistoryKeys
                if (Array.isArray(omitHistoryKeys) && omitHistoryKeys.length > 0) {
                    histories = histories.filter((history) => {
                        const deltaKeys = Object.keys(history?.deltas || {});
                        return !deltaKeys.some((key) => omitHistoryKeys.includes(key));
                    });
                }

                // sanitize unwanted fields (omitFields)
                histories = histories.map((history) => {
                    const sanitizedContent = { ...(history?.content ?? {}) };
                    const sanitizedDeltas = { ...(history?.deltas ?? {}) };

                    omitFields.forEach((key) => {
                        delete sanitizedContent[key];
                        delete sanitizedDeltas[key];
                    });

                    return {
                        ...(history ?? {}),
                        content: sanitizedContent,
                        deltas: sanitizedDeltas,
                    };
                });

                // filter out any empty / creation histories
                histories = histories.filter((history) => {
                    const isCreate = history?.event === 'create';
                    const hasContent = Object.keys(history?.content || {}).length > 0;
                    return !isCreate && hasContent;
                });

                setItemHistory(histories);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        };

        if (itemId) {
            fetchHistory();
        }
    }, [itemId]);

    if (!itemHistory?.length) {
        return <></>;
    }

    return (
        <Box display="flex" flexDirection="column" mt={2}>
            <Box px={2}>
                <Typography variant="h6">{label}</Typography>
            </Box>
            <HistoryList>
                {itemHistory.map((history) => {
                    const { created_at, content = {}, user } = history;

                    return (
                        <HistoryItem key={history.created_at}>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography variant="caption" color="text.secondary">
                                    {moment(created_at).format('DD MMM YYYY | hh:mm A')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {user}
                                </Typography>
                            </Box>

                            <Box display="flex" flexDirection="column" gap={1}>
                                {Object.entries(content).map((cont) => {
                                    const field = cont[0];
                                    const oldValue = cont[1]?.[0];
                                    const newValue = cont[1]?.[1];

                                    return (
                                        <Box key={field} display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2" fontWeight="bold">
                                                {formatFieldName(field, localization)}:
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {renderValue(oldValue)}
                                            </Typography>
                                            <Typography variant="body2">→</Typography>
                                            <Typography variant="body2" color="text.primary">
                                                {renderValue(newValue)}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </HistoryItem>
                    );
                })}
            </HistoryList>
        </Box>
    );
};

export default ItemEditHistory;
